"use client";

import { useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";

import Container from "apps/website/components/layout/Container/Container";
import {
  useSignupFlowStore,
} from "libs/state/src/lib/stores/signupFlow/hooks/useSignUpFlowStore";
import {
  MessagingCard,
} from "apps/website/components/feature/MessagingCard/MessagingCard";
import { ContentWrapper } from "apps/website/components/sign-up/fresh/common";
import { SignupFlowSlug, useFlowNavigation } from "@./state";
import QuoteBanner from "apps/website/components/form/QuoteBanner/QuoteBanner";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import Illustration from "apps/website/components/base/Illustration/Illustration";
import { StickyCTA } from "apps/website/components/feature/StickyCTA/StickyCTA";
import { scrollToContent } from "apps/website/utils/website/scrollToContent";
import { Polygon } from "apps/website/components/feature/Polygon/Polygon";
import RadioGroup from "apps/website/components/form/RadioButtons/RadioGroup/RadioGroup";
import { InputField } from "apps/website/components/form/Input/v2/InputField";
import TextBody from "apps/website/components/base/Text/TextBody/TextBody";
import TextSubtitle from "apps/website/components/base/Text/TextSubtitle/TextSubtitle";
import Icon from "apps/website/components/base/Icon/Icon";
import cx from "apps/website/utils/misc/cx";
import {
  createEmailContactLink,
  renderRichTranslations,
} from "apps/website/utils/translations/richText";
import { useEvent } from "libs/hooks/src/lib/event/useEvent";

import { useCatNamesNewForm } from "./hooks/useCatNamesNewForm";
import { CAT_COUNT_OPTIONS, PLACEHOLDER_MAP } from "./constants";
import {
  extractCatNamesFromStore,
  hasMultiCatDiscount,
  hasMoreThanFourCats,
  isRadioValid,
} from "./utils";
import { CatCount } from "./types";

export default function CatNamesNew() {
  const { createCats, cats } = useSignupFlowStore((state) => ({
    createCats: state.createCats,
    cats: state.cats,
  }));

  const storedCatNames = extractCatNamesFromStore(cats);
  const { dispatchEvent } = useEvent();

  const {
    selectedCatCount,
    catNames,
    errors,
    errorIndexes,
    showMoreThanFourCatsCard,
    setSelectedCatCount,
    updateName,
    validateAll,
    getFormattedCatEntries,
    toggleMoreThanFourCatsCard,
  } = useCatNamesNewForm(storedCatNames.filter((name) => name !== ""));

  const [ hasRadioError, setHasRadioError ] = useState(false);

  const hasInputErrors = Object.keys(errors).length > 0;
  const showInputs = selectedCatCount !== null;

  const t = useTranslations("SUFv2.CatNames");

  const { goToNext, canContinue } = useFlowNavigation(SignupFlowSlug.CAT_NAMES);

  useEffect(() => {
    dispatchEvent("cat_count", {
      category: "manage_fresh",
      label: "cat_count_interaction",
      cat_count: catNames.length,
      variant: "test",
    });
  }, [ catNames.length, dispatchEvent ]);

  const handleSubmit = () => {
    const isValid = validateAll();

    if (!isRadioValid(selectedCatCount)) {
      setHasRadioError(true);
      return;
    }

    if (isValid && canContinue) {
      const entries = getFormattedCatEntries();

      dispatchEvent("cat_count", {
        category: "manage_fresh",
        label: "cat_count_submitted",
        cat_count: entries.length,
        variant: "test",
      });

      createCats(entries);
      goToNext();
    }
  };

  const handleEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleInputChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    updateName(index, event.target.value);
  };

  const handleInputBlur = (index: number) => (event: React.FocusEvent<HTMLInputElement>) => {
    updateName(index, event.target.value);
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      if (hasRadioError) {
        scrollToContent("catNamesRadioError");
      } else if (hasInputErrors) {
        scrollToContent("catNamesErrorMessage");
      } else if (hasMoreThanFourCats(catNames.length) && showMoreThanFourCatsCard) {
        scrollToContent("catNamesMoreThanFourCats");
      } else if (hasMultiCatDiscount(catNames.length)) {
        scrollToContent("catNamesMultiCatDiscount");
      }
    });
  }, [ hasRadioError, hasInputErrors, showMoreThanFourCatsCard, catNames.length ]);

  return (
    <>
      <Container size="md" padding="none" className="relative z-10 pb-24">

        <div className="max-w-[393px] mx-auto">
          <QuoteBanner
            quote={t("quoteBanner.quoteNew")}
            author={t("quoteBanner.author")}
            image={{ src: "/images/people/Dominique.webp", width: 48, height: 58 }}
            styleOptions={{ bannerFillColour: "light", frameFillColour: "green" }}
          />
        </div>

        <Spacer size="sm" />

        <ContentWrapper
          backgroundColour="light-grey"
          styleOptions={{
            paddingX: 6,
            paddingY: 5,
            borderRadius: "md",
            direction: "column",
            justify: "center",
            align: "center",
            gapY: 3,
          }}
        >
          <RadioGroup
            name="catCount"
            selectedValue={selectedCatCount === 4 ? "4+" : selectedCatCount?.toString() || undefined}
            options={CAT_COUNT_OPTIONS}
            legend={t("radioLegend")}
            onChange={(value) => {
              setSelectedCatCount((value === "4+" ? 4 : Number(value)) as CatCount);
              setHasRadioError(false);
            }}
            styleOptions={{ columns: 4, gap: 2 }}
          />

          { hasRadioError && (
            <MessagingCard
              component="functional"
              description={t("errorMessages.radios")}
              icon={{ icon: "warningBold" }}
              styleOptions={{ backgroundColor: "light" }}
              id="catNamesRadioError"
            />
          ) }

          { showInputs && (
            <>
              <TextBody size={1} align="center">
                { t("catNameInput.legendNew") }
              </TextBody>

              <div className="w-full flex flex-col gap-3">
                { catNames.map((name, index) => {
                  const placeholderKey = PLACEHOLDER_MAP[index] || "placeholder";
                  const hasError = errorIndexes.has(index);
                  const inputId = `catName-${index}`;

                  return (
                    <InputField
                      key={inputId}
                      name={inputId}
                      srLabel={t("catNameInput.srLabel")}
                      value={name}
                      placeholder={t(`catNameInput.${placeholderKey}`)}
                      onChange={handleInputChange(index)}
                      onBlur={handleInputBlur(index)}
                      onKeyDown={handleEnterKeyDown}
                      error={hasError}
                      size="full"
                      styleOptions={{ textAlign: "center" }}
                    />
                  );
                }) }
              </div>
            </>
          ) }

          { hasInputErrors && (
            <MessagingCard
              component="functional"
              description={t("errorMessages.inputs")}
              icon={{ icon: "warningBold" }}
              styleOptions={{ backgroundColor: "light" }}
              id="catNamesErrorMessage"
            />
          ) }

          { hasMoreThanFourCats(catNames.length) && (
            <div className="w-full flex flex-col gap-3">
              <div className="flex justify-end">
                <button
                  onClick={toggleMoreThanFourCatsCard}
                  className="flex items-center gap-1 underline underline-offset-2 decoration-1 text-black"
                  type="button"
                >
                  <TextSubtitle size={3}>
                    { t("moreThanFourCats.button") }
                  </TextSubtitle>
                  <Icon
                    icon="chevronDownHeavy"
                    size="xsmall"
                    className={cx(
                      "transition-transform duration-200",
                      showMoreThanFourCatsCard && "rotate-180",
                    )}
                  />
                </button>
              </div>

              { showMoreThanFourCatsCard && (
                <MessagingCard
                  component="functional"
                  description={renderRichTranslations(
                    t,
                    "moreThanFourCats.cardBodyNew",
                    {
                      email: (chunks: React.ReactNode) => createEmailContactLink(
                        chunks,
                        "meow@katkin.com",
                        2,
                        "normal",
                      ),
                    },
                  )}
                  icon={{ icon: "informationHeavy", color: "default" }}
                  styleOptions={{ backgroundColor: "light" }}
                  id="catNamesMoreThanFourCats"
                />
              ) }
            </div>
          ) }

          { hasMultiCatDiscount(catNames.length) && (
            <MessagingCard
              component="functional"
              description={t("multiCatDiscountNew", { count: catNames.length })}
              icon={{ icon: "currency", color: "success" }}
              styleOptions={{ backgroundColor: "mid-green" }}
              id="catNamesMultiCatDiscount"
            />
          ) }

        </ContentWrapper>

      </Container>

      <Polygon
        className="fixed bottom-0 left-0 w-full md:h-[245px] h-[195px]"
        bgColor="sand-200"
        clipPath={{
          topLeft: "0% 15%",
          topRight: "100% 0%",
          bottomRight: "100% 100%",
          bottomLeft: "0% 100%",
        }}
      />

      <div className="w-full max-w-[315px] md:max-w-[380px] mx-auto fixed md:bottom-24 bottom-20 left-0 right-0 z-[2] overflow-clip" data-testid="fresh-start-illustration">
        <Illustration illustration="freshStart" size="fit" />
      </div>

      <div className="w-full relative">
        <StickyCTA
          position="fixed"
          button={{
            text: t("ctaButton"),
            onClick: handleSubmit,
            icon: { name: "arrowRightShort", position: "trailing" },
            color: "primary",
            design: "default",
            ariaLabel: t("ctaAriaLabel"),
            size: "full-width",
          }}
        />
      </div>
    </>
  );
}
